import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import { RootState, useAppDispatch } from "../../../redux/store";
import { NgwAvatar } from "../../../components/common";
import { ApprovalHistory } from "../../../components/notificationv2";
import { callPhpWorkflow } from "../../../redux/features/common";
import { getNotificationDetail } from "../../../redux/features/notifications";
import { DateTimeUtil } from "../../../utils";

const EmployeeExtraHoursNotificationDetail: React.FC = () => {
    const dispatch = useAppDispatch();

    const notificationDetail = useSelector((state: RootState) => state.notification.notificationDetail);
    const isLoading = useSelector((state: RootState) => state.notification.isNotificationDetailLoading);
    const isCallingWorkflow = useSelector((state: RootState) => state.common.isCallingWorkflow);
    const user = useSelector((state: RootState) => state.user.user);

    const [notes, setNotes] = useState("");
    const [isRefreshing, setIsRefreshing] = useState(false);

    const extraHours = notificationDetail?.timeoff_type_assigned_details;
    const approvalDetails = extraHours?.additional_json?.approval_details;
    const requestor = extraHours?.requestor;

    const currentStep = useMemo(() => {
        const currentStepCode = notificationDetail?.workflow_current_step_details?.step_code;
        if (!currentStepCode) return undefined;
        return notificationDetail?.workflow_details?.find((wf: any) => wf.step_code === currentStepCode);
    }, [notificationDetail?.workflow_current_step_details?.step_code, notificationDetail?.workflow_details]);

    const canAct =
        Boolean(currentStep) &&
        notificationDetail?.workflow_current_step_details?.current_status === "0" &&
        Boolean((currentStep as any)?.actor_json?.[user.id!]) &&
        notificationDetail?.only_notify_no_action !== "1";

    const refreshData = async () => {
        if (!notificationDetail?.notification_id || !user?.os_emp_account_id) return;
        setIsRefreshing(true);
        await dispatch(
            getNotificationDetail({
                notificationId: notificationDetail.notification_id,
                employeeId: Number(user.os_emp_account_id),
            })
        );
        setIsRefreshing(false);
    };

    const buildWorkflowBody = (status: 0 | 1) => {
        const referenceId = notificationDetail?.alert_json?.REF_ID;
        const approverKey = user?.os_emp_account_id?.toString() ?? "";

        if (!referenceId || !approverKey) return null;

        const referenceIdStr = referenceId.toString();

        return {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: referenceIdStr,
            result:
                status === 1
                    ? { APPROVED: { [approverKey]: [referenceIdStr] }, REJECTED: {} }
                    : { APPROVED: {}, REJECTED: { [approverKey]: [referenceIdStr] } },
            notes,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
        };
    };

    const handleApprove = async () => {
        const body = buildWorkflowBody(1);
        if (!body) return;
        await dispatch(callPhpWorkflow(body));
        refreshData();
    };

    const handleReject = async () => {
        const body = buildWorkflowBody(0);
        if (!body) return;
        await dispatch(callPhpWorkflow(body));
        refreshData();
    };

    if (isLoading) {
        return (
            <div className="flex flex-col justify-center items-center h-screen gap-2">
                <div className="animate-spin w-10 h-10 border-4 border-gray-300 border-t-transparent rounded-full" />
                {isRefreshing && <p className="text-gray-600 text-sm">Refreshing...</p>}
            </div>
        );
    }

    if (!notificationDetail) return null;

    const formatToMaxTwoDecimals = (value: unknown): string | null => {
        if (value == null) return null;
        const n = typeof value === "number" ? value : Number.parseFloat(String(value));
        if (Number.isNaN(n)) return null;
        return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
    };

    const requestHours = approvalDetails?.request_hours;

    const balance = approvalDetails?.current_balance ?? extraHours?.currrent_balance;
    const trackTimeIn = extraHours?.category?.track_time_in;
    const formattedBalance = formatToMaxTwoDecimals(balance);
    const formattedRequestHours = formatToMaxTwoDecimals(requestHours);

    return (
        <div className="bg-gray-50 min-h-screen pb-8">
            <div className="w-full md:w-1/2 mx-auto">
                <div className="bg-white mx-4 mt-4 rounded-lg shadow-sm p-4">
                    {/* Header */}
                    <div className="flex flex-col border-b pb-3">
                        <h2 className="text-xl font-semibold text-gray-900">
                            {notificationDetail?.message_info?.title ?? notificationDetail?.notification_display_name ?? "Extra Hours"}
                        </h2>
                        {notificationDetail?.message_info?.body && (
                            <p className="text-sm text-gray-600 mt-1">{notificationDetail.message_info.body}</p>
                        )}
                    </div>

                    {/* Requestor */}
                    <div className="mt-4 flex items-center gap-3">
                        <NgwAvatar src={requestor?.image_url} name={requestor?.name} />
                        <div className="min-w-0">
                            <p className="text-base font-semibold text-gray-900 truncate">{requestor?.name ?? "-"}</p>
                            {extraHours?.policy_name && (
                                <p className="text-sm text-gray-500 truncate">{extraHours.policy_name}</p>
                            )}
                        </div>
                    </div>

                    {/* Summary cards */}
                    <div className="mt-4 grid grid-cols-1 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <p className="text-xs text-gray-500">Requested hours</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {formattedRequestHours ?? "-"}
                            </p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <p className="text-xs text-gray-500">Current balance</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {formattedBalance != null ? `${formattedBalance}${trackTimeIn ? ` ${trackTimeIn}` : ""}` : "-"}
                            </p>
                        </div>

                        {(approvalDetails?.start_date || approvalDetails?.end_date) && (
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <p className="text-xs text-gray-500">Period</p>
                                <p className="text-sm font-medium text-gray-900">
                                    {approvalDetails?.start_date
                                        ? DateTimeUtil.FormatWithDayjsUtc(approvalDetails.start_date, DateTimeUtil.DATETIME_FANCY_2)
                                        : "-"}
                                    {"  "}to{"  "}
                                    {approvalDetails?.end_date
                                        ? DateTimeUtil.FormatWithDayjsUtc(approvalDetails.end_date, DateTimeUtil.DATETIME_FANCY_2)
                                        : "-"}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Approval history */}
                    {notificationDetail?.workflow_details && notificationDetail.workflow_details.length > 0 && (
                        <ApprovalHistory workflowDetails={notificationDetail.workflow_details as any[]} title="Approval History" />
                    )}

                    {/* Notes + actions */}
                    {canAct && (
                        <div className="mt-4">
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full border-2 rounded-md p-2"
                                placeholder="Add notes"
                            />

                            <div className="flex space-x-2 mt-4">
                                {isCallingWorkflow ? (
                                    <Loader2 className="animate-spin text-blue-500" />
                                ) : (
                                    <>
                                        <button
                                            onClick={handleApprove}
                                            className="flex-1 bg-white border border-blue-600 hover:bg-blue-50 text-blue-600 font-medium py-2 px-3 rounded-md transition-colors text-sm"
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={handleReject}
                                            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-3 rounded-md transition-colors text-sm"
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployeeExtraHoursNotificationDetail;

